// eslint-disable-next-line filenames/match-exported
import GlobalModel from 'echarts/lib/model/Global';
import ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import { eachAfter, eachBefore } from 'echarts/lib/chart/tree/traversalHelper';
import Tree from 'echarts/lib/data/Tree';
import {
  init,
  firstWalk,
  secondWalk,
  separation as sep,
  getViewRect
} from 'echarts/lib/chart/tree/layoutHelper';
import { graphic } from 'echarts';
import type { BoundingRect } from 'zrender';
import { createSymbol } from 'echarts/lib/util/symbol';
import {
  RelationshipDiagramSeriesModelClazz as RelationshipDiagramModel,
  RelationshipDiagramSeriesNodeItemOption,
  RelationTreeNode,
  GroupNodeArray
} from './RelationshipDiagramSeries';
import { createEl } from './util';
import { measureTextWidth } from '../../../util/utils';

export interface RelationTreeLayoutNode extends RelationTreeNode {
  parentNode: RelationTreeLayoutNode;
  // eslint-disable-next-line no-use-before-define
  hierNode: HierNode;
  children: RelationTreeLayoutNode[];
}

interface HierNode {
  defaultAncestor: RelationTreeLayoutNode;
  ancestor: RelationTreeLayoutNode;
  prelim: number;
  modifier: number;
  change: number;
  shift: number;
  i: number;
  thread: RelationTreeLayoutNode;
  parentPlace: number;
}

/** 基准字体尺寸 */
const BASE_FONT_SIZE = 10;
/** 层级控制 */
const NODE_LINE_Z = 5;
/** 背景颜色层级 */
const BACKGROUND_COLOR_Z = -9999;
/** 水印层级 */
const WATERMARK_Z = 9999;
/** 箭头宽 */
const ARROW_WIDTH = 5;
/** 箭头高 */
const ARROW_HEIGHT = BASE_FONT_SIZE;
/** 导向线离节点的距离 */
const TO_NEXT_TREE_LINE_TOP = 8;
/** 常规线颜色 */
const LINE_STROKE_COLOR = 'rgba(0, 0, 0, 0.24)';
/** 计算line包围盒的补偿 */
const LINE_OFFSET = 2.5;
/** 线宽 */
const LINE_WIDTH = 0.5;

// 创建同级分支线信息
function createLevelLine(seriesModel: RelationshipDiagramModel, api: ExtensionAPI) {
  if (!seriesModel.option.dvLevelLine || !seriesModel.option.dvLevelLine.show) {
    return;
  }
  const { groupNodeArray, trees, treeMaxY } = seriesModel;
  const windowRectHeight = api.getHeight();
  const levelLine = [];
  Object.keys(groupNodeArray).forEach(name => {
    // 只有同列的组节点中至少有一个为展开状态时，才会出现分支线
    const isExpandNode: RelationTreeNode = groupNodeArray[name].find(
      node => node.isExpand === true
    );
    if (!isExpandNode) {
      return;
    }
    const isExpandNodeData = isExpandNode.hostTree.data.getRawDataItem(
      isExpandNode.dataIndex
    ) as RelationshipDiagramSeriesNodeItemOption;
    const isExpandNodeInfo = isExpandNode.getLayout() as BoundingRect;
    // 为分支线的上下左右的位置布局做处理
    let linePadding: number[] = seriesModel.option.dvLevelLine.padding(
      isExpandNodeData,
      isExpandNode
    );
    if (!linePadding) {
      linePadding = [0, 0, 0, 0];
    }

    // 背景的padding
    const backgroundPadding = seriesModel.option?.dvBackground?.padding || [0, 0, 0, 0];
    const lineStyle = seriesModel.option?.dvLevelLine?.style;
    /**
     * ZRender在计算line的包围盒时会在x和y方向多出2.5像素
     * y2 = y方向最大偏移 + 自身偏移 + 背景padding - 2.5
     */
    let y2: number =
      treeMaxY[treeMaxY.length - 1] + linePadding[2] + backgroundPadding[2] - LINE_OFFSET;

    // 当关系图的内容高度小于视窗高度时，分支线会拉长到视窗底部
    if (treeMaxY[treeMaxY.length - 1] < windowRectHeight) {
      y2 = windowRectHeight + linePadding[2] - LINE_OFFSET;
    }

    const line = {
      x: isExpandNodeInfo.x,
      y: 0,
      levelLine: {
        type: 'line',
        shape: {
          x1: 0 + linePadding[3] || -linePadding[1],
          y1: LINE_OFFSET + linePadding[0],
          x2: 0 + linePadding[3] || -linePadding[1],
          y2
        },
        style: lineStyle || {
          lineWidth: LINE_WIDTH,
          stroke: LINE_STROKE_COLOR,
          lineDash: [10, 5]
        },
        z: NODE_LINE_Z
      }
    };
    levelLine.push(line);
  });
  const { data } = trees[0] as unknown as Tree;
  data.setLayout({
    levelLine
  });
}

// 创建导向线信息
function createToNextTreeLine(seriesModel: RelationshipDiagramModel) {
  const { treeMaxY, treesLength } = seriesModel;
  seriesModel.trees.forEach(tree => {
    const { data } = tree;
    const treeFakeVirtualRoot = tree.root as unknown as RelationTreeNode;
    const treeFakeRealRoot = treeFakeVirtualRoot.children[0] as RelationTreeNode;
    const userOption = seriesModel.option?.dvToNextTreeLine;
    if (!userOption || !userOption.show) {
      return;
    }

    const toNextTreeLine = Array.from({ length: treesLength }).map((_, index) => {
      // 最后一棵子树没有导向线
      if (treesLength === index + 1) {
        return null;
      }

      const subtreeRealRootRect = (
        treeFakeRealRoot.children[index] as RelationTreeNode
      ).nodeElGroup.getBoundingRect() as BoundingRect;
      const subtreeRealRootInfo = treeFakeRealRoot.children[index].getLayout() as BoundingRect;
      const toNextTreeLineY =
        subtreeRealRootInfo.y + subtreeRealRootRect.height + TO_NEXT_TREE_LINE_TOP;
      /** 导向线起始点距离节点的距离 */
      const offsetY = seriesModel.option?.dvVerticalSpacingBetweenNode[0] || 0;
      /** 箭头的大小比例 */
      const arrowScale = userOption?.arrowStyle?.scale || 1;
      return {
        x: subtreeRealRootInfo.x + subtreeRealRootRect.width / 2,
        y: 0,
        toNextTreeLine: {
          type: 'line',
          shape: {
            x1: 0,
            y1: toNextTreeLineY,
            x2: 0,
            y2: treeMaxY[index + 1] + offsetY / 2
          },
          style: !Object.getOwnPropertyNames(userOption?.lineStyle || {}).length
            ? {
                lineWidth: LINE_WIDTH,
                stroke: LINE_STROKE_COLOR
              }
            : userOption?.lineStyle,
          z: NODE_LINE_Z
        },
        arrow: {
          type: 'polygon',
          shape: {
            points: [
              [ARROW_WIDTH * arrowScale, treeMaxY[index + 1] + offsetY / 2],
              [0, treeMaxY[index + 1] + ARROW_HEIGHT * arrowScale + offsetY / 2],
              [-ARROW_WIDTH * arrowScale, treeMaxY[index + 1] + offsetY / 2]
            ]
          },
          style: !Object.getOwnPropertyNames(userOption?.arrowStyle || {}).length
            ? {
                fill: LINE_STROKE_COLOR,
                lineWidth: LINE_WIDTH
              }
            : userOption?.arrowStyle,
          z: NODE_LINE_Z
        }
      };
    });

    data.setLayout({
      toNextTreeLine
    });
  });
}

// 创建水印信息
function createWatermark(seriesModel: RelationshipDiagramModel) {
  // 用户配置项
  const watermarkConfig = seriesModel.option?.dvWatermark;
  const { treeMaxX, treeMaxY, treesLength } = seriesModel;
  if (!watermarkConfig || !watermarkConfig.show) {
    return;
  }
  seriesModel.trees.forEach(tree => {
    const { data } = tree;

    // 根据假根节点下子树数量创建子树的水印信息
    const watermark = Array.from({ length: treesLength }, (_, index) => {
      const { name, style } = watermarkConfig.watermark[index];
      const watermarkLength = measureTextWidth(
        style.text,
        Number(style.fontSize),
        style.fontFamily,
        style.fontWeight
      );
      return {
        // 水印垂直居中于子树块内部
        x: (Math.max(...treeMaxX) - watermarkLength) / 2,
        y: (treeMaxY[index + 1] + treeMaxY[index] - Number(style.fontSize)) / 2,
        watermarkText: {
          name,
          type: 'text',
          style: {
            x: 0,
            y: 0,
            ...style
          },
          silent: true,
          z: WATERMARK_Z
        }
      };
    });

    data.setLayout({
      watermark
    });
  });
}

// 创建背景颜色信息
function createBackground(seriesModel: RelationshipDiagramModel, api: ExtensionAPI) {
  seriesModel.trees.forEach(tree => {
    const { dvBackground } = seriesModel.option;
    const { treeMaxX, treeMaxY, treesLength } = seriesModel;
    const windowRectHeight = api.getHeight();
    if (!dvBackground || !dvBackground.show) {
      return;
    }
    const { data } = tree;

    const background = Array.from({ length: treesLength }, (_, index) => {
      const offsetY = seriesModel.option?.dvVerticalSpacingBetweenNode[0] || 0;
      /** 背景的padding */
      const backgroundPadding = dvBackground.padding || [0, 0, 0, 0];
      /** 背景高度 */
      let height = treeMaxY[index + 1] - treeMaxY[index];
      // 最后一颗子树的背景高度进行负补偿，其他子树进行正补偿
      if (index === treesLength - 1) {
        height -= offsetY / 2 - backgroundPadding[2];
      } else {
        height += offsetY / 2 + LINE_OFFSET;
      }
      // 当关系图内容高度小于视窗高度时，保证最后一棵树的背景高度能填满视窗
      if (index === treesLength - 1 && treeMaxY[index + 1] < windowRectHeight) {
        height += windowRectHeight - treeMaxY[index + 1] - offsetY;
      }
      return {
        x: 0,
        y:
          index === 0
            ? treeMaxY[index]
            : treeMaxY[index] + seriesModel.option.dvVerticalSpacingBetweenNode[0] / 2 || 0,
        rect: {
          type: 'rect',
          shape: {
            x: 0,
            y: 0,
            width: Math.max(...treeMaxX) + backgroundPadding[1],
            height
          },
          style: {
            fill: dvBackground.color[index]
          },
          z: BACKGROUND_COLOR_Z
        }
      };
    });

    data.setLayout({
      background
    });
  });
}

// 标记节点的index属性
function initializeNodeByIndex(node: RelationTreeNode, index: number) {
  node.index = index;
}

// 遍历节点
function traverseNodes(root: RelationTreeNode) {
  const nodes: RelationTreeNode[] = [root];
  let node: RelationTreeNode;
  while ((node = nodes.pop())) {
    if (node.isExpand) {
      const { children } = node;
      if (children.length) {
        for (let i = children.length - 1; i >= 0; i--) {
          initializeNodeByIndex(children[i], i);
          nodes.push(children[i]);
        }
      }
    }
  }
}

/**
 * 基于ECharts源码进行扩展，为每一个子节点标记上index属性，知道是一个节点下的第几个子节点.
 * @see https://github.com/apache/echarts/blob/69cab1f64ebbae9c7ce5f6dd37129e701549d092/src/chart/tree/traversalHelper.ts#L54
 */
function customEachBefore(root: RelationTreeNode) {
  // 对根节点单独进行index的初始化
  initializeNodeByIndex(root, 0);
  traverseNodes(root);
}

function updateGroupNodeArray(seriesModel: RelationshipDiagramModel, node: RelationTreeNode) {
  const { groupNodeArray } = seriesModel;
  // node.depth为1是假节点不做渲染,加入depth为奇数的节点
  if (`${(node.depth - 1) / 2 - 1}` in groupNodeArray) {
    groupNodeArray[(node.depth - 1) / 2 - 1].push(node);
  }
}

// 计算节点自身宽度与节点间间距的数组
function calculateNodeGap(
  realRoot: RelationTreeLayoutNode,
  seriesModel: RelationshipDiagramModel
): {
  spaceArray: number[];
  widthArray: number[];
} {
  const spaceArray: number[] = [0];
  const widthArray: number[] = [0];

  eachBefore(realRoot, (node: RelationTreeNode) => {
    updateGroupNodeArray(seriesModel, node);
    const item = node.hostTree.data.getRawDataItem(
      node.dataIndex
    ) as RelationshipDiagramSeriesNodeItemOption;
    // 存放的是绘制后的图形的右边距离下一个节点的实际坐标的距离
    spaceArray[node.depth - 1] = spaceArray[node.depth - 1] || 0;
    // 存放的是当前节点的宽度
    widthArray[node.depth - 1] = widthArray[node.depth - 1] || 0;
    // 节点横向间距设置
    seriesModel.option.dvNodeGap(item, node, spaceArray, widthArray);
  });
  return {
    spaceArray,
    widthArray
  };
}

// 为节点绑定图形元素和连接线元素（绘图阶段可以直接使用）
function bindingElement(
  node: RelationTreeNode,
  seriesModel: RelationshipDiagramModel,
  coorY: number,
  coorX: number
) {
  const item = node.hostTree.data.getRawDataItem(
    node.dataIndex
  ) as RelationshipDiagramSeriesNodeItemOption;
  // 为每个节点绑定自身的节点图形
  const nodeElGroup = seriesModel.option.dvNodeRender(
    item,
    createEl,
    new graphic.Group({ name: 'node', x: coorX, y: coorY }),
    createSymbol,
    node
  );
  node.nodeElGroup = nodeElGroup;

  // 为节点绑定连接线
  const lineElGroup = seriesModel.option.dvLineRender(
    item,
    createEl,
    new graphic.Group({ name: 'line', x: coorX, y: coorY }),
    node
  );
  node.lineElGroup = lineElGroup;
}

function getDifferentTreeOffset(
  nodeCoorYArray: number[],
  seriesModel: RelationshipDiagramModel
): number {
  // 背景的padding
  const backgroundPadding = seriesModel.option?.dvBackground?.padding || [0, 0, 0, 0];
  const maxY = Math.max(...nodeCoorYArray);
  const verticalSpacingBetweenNode = seriesModel.option?.dvVerticalSpacingBetweenNode || [0];
  // 不同树间的间隔
  return maxY === 0 ? backgroundPadding[0] : maxY + verticalSpacingBetweenNode[0];
}

function setNodeCoorYAndCoorX(
  node: RelationTreeNode,
  maxYFromThisDepth: number,
  offsetArray: number[],
  differentTreeOffset: number,
  seriesModel: RelationshipDiagramModel
): {
  coorX: number;
  coorY: number;
} {
  const backgroundPadding = seriesModel.option?.dvBackground?.padding || [0, 0, 0, 0];
  // x方向要加背景的padding偏移
  const coorX = offsetArray[node.depth - 1] + backgroundPadding[3];
  // 第一棵树y方向要加背景padding偏移，其他树要加树间距偏移
  /**
   * ZRender在计算line的包围盒时会在x和y方向多出2.5像素,所以这里在y方向补偿 LINE_OFFSET = 2.5
   */
  let coorY = (node.getLayout() as BoundingRect).x + differentTreeOffset + LINE_OFFSET;
  if (node.depth - 1 !== 1) {
    // 父节点的第一个子节点（y值和父节点相同）
    if (node.index === 0) {
      coorY = (node.parentNode.getLayout() as BoundingRect).y;
      // 首节点的其他子节点（y值要进行偏移）
    } else {
      // 每棵树的节点加上竖直方向上节点间的间距
      const verticalSpacingBetweenNode =
        seriesModel.option.dvVerticalSpacingBetweenNode ||
        Array.from({ length: Math.ceil(node.depth / 2) }, () => {
          return 0;
        });
      coorY = maxYFromThisDepth + verticalSpacingBetweenNode[Math.floor(node.depth / 2) - 1];
    }
  }
  node.setLayout({
    y: coorY,
    x: coorX
  });
  return {
    coorX,
    coorY
  };
}

function updateNodeCoorArray(
  node: RelationTreeNode,
  maxYFromThisDepth: number,
  nodeCoorYArray: number[],
  nodeCoorXArray: number[],
  coorX: number,
  coorY: number
) {
  // 计算当前节点为止的最大y、x方向的偏移量
  const nodeRect = node.nodeElGroup.getBoundingRect().clone();
  nodeRect.applyTransform(node.nodeElGroup.transform);

  // 添加到当前节点为止的最大值
  nodeCoorYArray.push(Math.max(maxYFromThisDepth, coorY + nodeRect.height + nodeRect.y));
  nodeCoorXArray.push(Math.max(Math.max(...nodeCoorXArray), coorX + nodeRect.width + nodeRect.x));
}

// 计算假节点下每棵子树的节点的x、y位置信息
function createSubtree(
  realRoot: RelationTreeLayoutNode,
  seriesModel: RelationshipDiagramModel,
  offsetArray: number[]
) {
  // 每棵树y方向的长度，初始时为0
  const treeMaxY = [0];
  // 每棵树x方向的宽度，初始时为0
  const treeMaxX = [0];
  // 计算当前树的节点的最大y方向高度（包含节点本身高度）
  let nodeCoorYArray = [0];
  // 计算当前树的节点的最大x方向宽度（包含节点自身的宽度）
  let nodeCoorXArray = [0];
  realRoot.children.forEach(realChildrenRoot => {
    realChildrenRoot.setLayout({
      x: 0
    });
    nodeCoorYArray = [Math.max(...nodeCoorYArray)];
    nodeCoorXArray = [Math.max(...nodeCoorXArray)];
    const differentTreeOffset = getDifferentTreeOffset(nodeCoorYArray, seriesModel);
    eachBefore(realChildrenRoot, (node: RelationTreeNode) => {
      // 当前节点的最大y方向高度是由当前depth开始计算（之前的depth加入会导致间距误差， depth为1是假节点，所以要将depth-1）
      const maxYFromThisDepth = Math.max(...nodeCoorYArray.slice(node.depth - 1));
      const { coorX, coorY } = setNodeCoorYAndCoorX(
        node,
        maxYFromThisDepth,
        offsetArray,
        differentTreeOffset,
        seriesModel
      );
      // 为节点绑定图形元素
      bindingElement(node, seriesModel, coorY, coorX);
      updateNodeCoorArray(node, maxYFromThisDepth, nodeCoorYArray, nodeCoorXArray, coorX, coorY);
    });
    treeMaxY.push(Math.max(...nodeCoorYArray));
    treeMaxX.push(Math.max(...nodeCoorXArray));
  });
  // 保存每棵树的最大x、y偏移距离
  seriesModel.treeMaxY = treeMaxY;
  seriesModel.treeMaxX = treeMaxX;
}

/**
 * @param virtualRoot 假根节点的虚拟父节点
 * @param realRoot 假根节点（不参与渲染）
 * @see 使用的ECharts源码方法进行节点结构的初始化,详细可参考: https://github.com/apache/echarts/blob/f1d9eb54707dc79943df613a33e93873bffffb79/src/chart/tree/traversalHelper.ts#L25
 */
function initNodeLayoutInfo(virtualRoot: RelationTreeLayoutNode, realRoot: RelationTreeLayoutNode) {
  // 初始化所有计算信息
  init(virtualRoot);
  eachAfter(realRoot, firstWalk, sep());
  customEachBefore(realRoot);
  virtualRoot.hierNode.modifier = -realRoot.hierNode.prelim;
  eachBefore(realRoot, secondWalk);
}

// 计算节点的x方向间距数组
function calculateCoordinateXArray(widthArray: number[], spaceArray: number[]): number[] {
  let offset = 0;
  // 计算每个节点的x位置信息
  return widthArray.map((item, index) => {
    offset += item + spaceArray[index];
    return offset;
  });
}

// 从假节点开始计算节点的结构信息
function layoutTree(
  virtualRoot: RelationTreeLayoutNode,
  realRoot: RelationTreeLayoutNode,
  seriesModel: RelationshipDiagramModel
) {
  // 只有当假根节点存在时，才进行后续的布局计算
  if (realRoot) {
    initNodeLayoutInfo(virtualRoot, realRoot);
    const { widthArray, spaceArray } = calculateNodeGap(realRoot, seriesModel);
    const offsetArray = calculateCoordinateXArray(widthArray, spaceArray);
    // 计算假节点下每棵子树的节点的x、y位置信息
    createSubtree(realRoot, seriesModel, offsetArray);
  }
}

function initViewRect(seriesModel: RelationshipDiagramModel, api: ExtensionAPI) {
  const layoutInfo = getViewRect(seriesModel, api);
  seriesModel.layoutInfo = layoutInfo;
}

function clearOldLayoutInfo(seriesModel: RelationshipDiagramModel) {
  // ! 每次重绘都需要先清除上一次的布局信息
  seriesModel.trees.forEach(tree => {
    tree.clearLayouts();
  });
}

// 收集的都是node.depth为2之后的奇数节点（一般作为组节点控制节点的展开收起），可用于一键展开和收起所需要的遍历组节点使用
function initGroupNodeArray(seriesModel: RelationshipDiagramModel) {
  // 按列收集组节点（node.depth为奇数的节点）
  const groupNodeArray: GroupNodeArray = {};
  Array.from({ length: Math.ceil(seriesModel.maxDepth / 2 - 1) }, (_, i) => {
    groupNodeArray[i] = [];
    seriesModel.groupNodeArray = groupNodeArray;
  });
}

// 第一个节点是假的节点，为的是将多棵树放在一棵树中（之前多棵树的方案会使得数据的初始化出错，体现在ECharts提供的click事件获取的节点信息是错的）
function getFakeRoot(seriesModel: RelationshipDiagramModel) {
  const treeFakeVirtualRoot = seriesModel.trees[0].root as unknown as RelationTreeLayoutNode;
  // 假根节点不参与渲染，而是渲染假根节点下的子树
  const treeFakeRealRoot = treeFakeVirtualRoot.children[0];
  // 记录当前假根节点下有多少子节点（树）
  seriesModel.treesLength = treeFakeRealRoot.children.length;
  return { treeFakeVirtualRoot, treeFakeRealRoot };
}

// 关系图的基础布局函数，定义了节点、背景、水印、导向线、分支线的位置信息
function commonLayout(seriesModel: RelationshipDiagramModel, api: ExtensionAPI) {
  initViewRect(seriesModel, api);
  clearOldLayoutInfo(seriesModel);
  initGroupNodeArray(seriesModel);
  const { treeFakeVirtualRoot, treeFakeRealRoot } = getFakeRoot(seriesModel);
  seriesModel.trees.forEach(() => {
    layoutTree(treeFakeVirtualRoot, treeFakeRealRoot, seriesModel);
  });
  createBackground(seriesModel, api);
  createWatermark(seriesModel);
  createToNextTreeLine(seriesModel);
  createLevelLine(seriesModel, api);
}

export default function relationshipDiagramLayout(ecModel: GlobalModel, api: ExtensionAPI) {
  ecModel.eachSeriesByType(
    'dvRelationshipDiagram',
    function (seriesModel: RelationshipDiagramModel) {
      commonLayout(seriesModel, api);
    }
  );
}
